import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const ETIQUETAS_RETIE: Record<string, string> = {
  transformador: "Transformador",
  medidor: "Medidor",
  tc: "TC (transformador de corriente)",
  tp: "TP (transformador de potencial)",
};

function etiquetaRetieTipo(tipo: string): string {
  const m = String(tipo).match(/^retie_(transformador|medidor|tc|tp)_(\d+)$/i);
  if (!m) return tipo.replace(/_/g, " ");
  return `${ETIQUETAS_RETIE[m[1].toLowerCase()] || m[1]} #${m[2]}`;
}

type Props = {
  interventorAsignacion: Record<string, any>;
  retieWorkflow: Record<string, any>;
  documentosPorTipo: Record<string, any>;
  guardando: string | null;
  getValidacion: (tipo: string) => { estado?: string; observacion?: string; fecha?: string } | null;
  onRevisar: (tipo: string) => void;
  onVisualizar: (tipo: string) => void;
  onDescargar: (tipo: string) => void;
};

export default function RetieRevisionPanel({
  interventorAsignacion,
  retieWorkflow,
  documentosPorTipo,
  guardando,
  getValidacion,
  onRevisar,
  onVisualizar,
  onDescargar,
}: Props) {
  const tipos = Array.isArray(retieWorkflow?.tipos_requeridos)
    ? retieWorkflow.tipos_requeridos.map((t: string) => String(t))
    : [];
  const cantidades = retieWorkflow?.cantidades || {};
  const estado = String(retieWorkflow?.estado || "pendiente_cantidades");
  const interventor =
    interventorAsignacion?.interventor_nombre ||
    interventorAsignacion?.interventor_usuario ||
    "—";

  const chipEstadoWorkflow = (() => {
    if (estado === "completo") return { label: "RETIE completo", color: "success" as const };
    if (estado === "pendiente_revision") return { label: "Pendiente revisión", color: "warning" as const };
    if (estado === "pendiente_carga") return { label: "Pendiente carga solicitante", color: "info" as const };
    return { label: "Pendiente declaración de equipos", color: "default" as const };
  })();

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <Alert severity="info">
          Revise uno a uno los soportes RETIE cargados por el solicitante (transformadores, medidores, TC y TP).
          Apruebe o rechace cada archivo; los rechazados pueden volver a cargarse en el portal.
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Interventor asignado:
            </Typography>
            <Typography variant="body2">{interventor}</Typography>
            <Chip size="small" label={chipEstadoWorkflow.label} color={chipEstadoWorkflow.color} />
          </Stack>
          {cantidades && typeof cantidades === "object" && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Equipos declarados: {Number(cantidades.transformadores || 0)} transformador(es),{" "}
              {Number(cantidades.medidores || 0)} medidor(es), {Number(cantidades.tc || 0)} TC,{" "}
              {Number(cantidades.tp || 0)} TP
            </Typography>
          )}
          {!tipos.length ? (
            <Typography variant="body2" color="text.secondary">
              El solicitante aún no ha declarado las cantidades de equipos en el portal de acceso.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {tipos.map((tipo: string) => {
                const doc = documentosPorTipo[tipo];
                const val = getValidacion(tipo);
                const estadoDoc = val?.estado || (doc?.id ? "Pendiente" : "Sin cargar");
                return (
                  <Paper key={tipo} variant="outlined" sx={{ p: 1.2 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography fontWeight={700}>{etiquetaRetieTipo(tipo)}</Typography>
                        {doc?.nombre_original && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {doc.nombre_original}
                          </Typography>
                        )}
                        {val?.observacion && estadoDoc === "Desaprobado" && (
                          <Typography variant="caption" color="error" display="block">
                            {val.observacion}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!doc?.id || guardando !== null}
                            onClick={() => onRevisar(tipo)}
                          >
                            Revisar y validar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!doc?.id || guardando !== null}
                            onClick={() => onVisualizar(tipo)}
                          >
                            Visualizar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!doc?.id || guardando !== null}
                            onClick={() => onDescargar(tipo)}
                          >
                            Descargar
                          </Button>
                          <Chip
                            size="small"
                            label={estadoDoc}
                            color={
                              estadoDoc === "Aprobado"
                                ? "success"
                                : estadoDoc === "Desaprobado"
                                  ? "error"
                                  : estadoDoc === "Pendiente"
                                    ? "warning"
                                    : "default"
                            }
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Grid>
      {Array.isArray(retieWorkflow?.historial) && retieWorkflow.historial.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Trazabilidad RETIE
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: "auto" }}>
            {retieWorkflow.historial.slice(-15).map((h: any, i: number) => (
              <Typography key={i} variant="caption" color="text.secondary" display="block">
                {h.fecha ? new Date(h.fecha).toLocaleString("es-CO") : "—"} — {String(h.accion || "")}
                {h.tipo_documento ? ` · ${etiquetaRetieTipo(h.tipo_documento)}` : ""}
                {h.observacion ? ` · ${h.observacion}` : ""}
                {h.detalle ? ` · ${h.detalle}` : ""}
              </Typography>
            ))}
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
